import { PromptCollection } from '../types';
import { createApiCall, makeHeaders } from '../../../api/api';
import { appConfig } from '../../../appConfig';

const API_HOST = appConfig.api.baseUrl;
export const API_URL = API_HOST.replace(/\/$/, '') + '/api/';

type ApiResponse<T> = {
  data?: T;
  error?: string;
  status: number;
};

async function handleApiResponse<T>(response: Response, onUnauthorized: () => void): Promise<ApiResponse<T>> {
  if (response.status >= 400 && response.status < 500) {
    onUnauthorized();
    return {
      error: 'Unauthorized',
      status: response.status,
    };
  }

  if (response.status >= 500) {
    return {
      error: 'Server error occurred',
      status: response.status,
    };
  }

  try {
    const data = await response.json();
    return {
      data,
      status: response.status,
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      status: response.status,
    };
  }
}

export const promptService = {
  // Get all prompt collections
  getPromptCollections: createApiCall<PromptCollection[]>(async (_, onUnauthorized) => {
    const response = await fetch(`${API_URL}promptcollections`, {
      method: 'GET',
      headers: makeHeaders(),
    });
    return handleApiResponse(response, onUnauthorized);
  }),

  // Get a specific prompt collection by id
  getPromptCollection: createApiCall<PromptCollection, string>(async (collectionId, onUnauthorized) => {
    const response = await fetch(`${API_URL}promptcollections/${encodeURIComponent(collectionId)}`, {
      method: 'GET',
      headers: makeHeaders(),
    });
    return handleApiResponse(response, onUnauthorized);
  }),

  // Get all available prompt collections for dropdown
  getPromptCollectionsForDropdown: createApiCall<{ id: string; name: string }[]>(async (_, onUnauthorized) => {
    const response = await fetch(`${API_URL}promptcollections`, {
      method: 'GET',
      headers: makeHeaders(),
    });
    
    const result = await handleApiResponse<PromptCollection[]>(response, onUnauthorized);
    if (result.data) {
      return {
        data: result.data.map(collection => ({ id: collection.id, name: collection.name })),
        status: result.status,
      };
    }
    return {
      error: result.error,
      status: result.status,
    };
  }),

  // Save a prompt collection
  savePromptCollection: createApiCall<unknown, { collectionId: string; fileTexts: Record<string, string> }>(
    async ({ collectionId, fileTexts }, onUnauthorized) => {
      // First get the current collection to preserve structure
      const getResponse = await fetch(`${API_URL}promptcollections/${encodeURIComponent(collectionId)}`, {
        method: 'GET',
        headers: makeHeaders(),
      });
      
      const getResult = await handleApiResponse<PromptCollection>(getResponse, onUnauthorized);
      if (!getResult.data) {
        return getResult;
      }

      const currentCollection = getResult.data;

      // Update the content of each file
      const updatedFiles = currentCollection.files.map(file => ({
        ...file,
        content: fileTexts[file.name] || file.content || ''
      }));

      // Send the complete updated collection to the backend
      const response = await fetch(`${API_URL}promptcollections/${encodeURIComponent(collectionId)}`, {
        method: 'PUT',
        headers: makeHeaders(),
        body: JSON.stringify({
          files: updatedFiles
        }),
      });

      return handleApiResponse(response, onUnauthorized);
    }
  ),

  // Update a specific file within a collection
  updatePromptFile: createApiCall<unknown, { collectionId: string; fileName: string; content: string }>(
    async ({ collectionId, fileName, content }, onUnauthorized) => {
      const response = await fetch(`${API_URL}promptcollections/${encodeURIComponent(collectionId)}/files/${encodeURIComponent(fileName)}`, {
        method: 'PUT',
        headers: makeHeaders(),
        body: JSON.stringify({ content }),
      });

      return handleApiResponse(response, onUnauthorized);
    }
  )
}; 